"use client";

import { useCallback, useEffect, useId, useRef, useState } from "react";
import Link from "next/link";
import { useT } from "./LocaleProvider";

type FacetItem = { value: string; label: string; count: number };
export type FacetSet = {
  brand: FacetItem[];
  model: FacetItem[];
  collab: FacetItem[];
  size: FacetItem[];
};

/** The featured-drop cell — sourced from the Wix "New Drop" category server-side
 *  (lib/catalog.ts getFeaturedDrop) so it tracks the homepage hero. */
export type FeaturedCell = {
  /** storefront slug — builds the /shop/<id> link */
  id: string;
  /** full descriptive product name (the display title) */
  title: string;
  /** preformatted size run, e.g. "8–12" */
  sizesLabel: string;
};

/**
 * Desktop mega menu — hover/focus-to-reveal full-width panel under the header.
 *
 * Trigger: "SHOP" button. Opens on click; also opens on hover after a short
 * delay (so a passing mouse doesn't pop it open). Closes on:
 *   - click outside
 *   - Escape
 *   - link click (navigation)
 *   - mouseleave (after 200ms grace)
 *
 * Accessibility:
 *   - Button has aria-expanded + aria-controls + aria-haspopup
 *   - Panel uses role="menu" with role="menuitem" children
 *   - First link receives focus on click-open (not hover-open) so screen
 *     readers + keyboard users get a usable affordance
 */
export function MegaMenu({ facets, featured }: { facets: FacetSet; featured?: FeaturedCell }) {
  const [open, setOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);
  const panelId = useId();
  const closeTimer = useRef<number | null>(null);
  const t = useT();

  const close = useCallback(() => setOpen(false), []);

  // Click outside
  useEffect(() => {
    if (!open) return;
    function onDoc(e: MouseEvent) {
      if (!containerRef.current?.contains(e.target as Node)) close();
    }
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, [open, close]);

  // Escape to close
  useEffect(() => {
    if (!open) return;
    function onKey(e: KeyboardEvent) {
      if (e.key === "Escape") {
        close();
        buttonRef.current?.focus();
      }
    }
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [open, close]);

  function cancelClose() {
    if (closeTimer.current) {
      window.clearTimeout(closeTimer.current);
      closeTimer.current = null;
    }
  }

  function scheduleClose() {
    cancelClose();
    closeTimer.current = window.setTimeout(() => setOpen(false), 200);
  }

  return (
    <div
      ref={containerRef}
      className="relative"
      onMouseEnter={() => {
        cancelClose();
        setOpen(true);
      }}
      onMouseLeave={scheduleClose}
    >
      <button
        ref={buttonRef}
        type="button"
        aria-haspopup="menu"
        aria-expanded={open}
        aria-controls={panelId}
        onClick={() => setOpen((v) => !v)}
        className="inline-flex items-center gap-1.5 text-[11px] uppercase tracking-[0.18em] hover:text-[var(--color-foil)]"
      >
        Shop
        <Chevron className={`h-2.5 w-2.5 transition-transform ${open ? "rotate-180" : ""}`} />
      </button>

      <div
        id={panelId}
        ref={panelRef}
        role="menu"
        aria-label="Shop catalog"
        className={`fixed left-0 right-0 top-[var(--mh-h,4.25rem)] z-40 border-y border-[var(--color-bone)]/15 bg-[var(--color-ink)] shadow-2xl transition-[opacity,transform] duration-200 ${
          open ? "pointer-events-auto translate-y-0 opacity-100" : "pointer-events-none -translate-y-1 opacity-0"
        }`}
      >
        <div className="mx-auto grid max-w-[1400px] grid-cols-12 gap-8 px-6 py-10">
          {/* Column: Brand */}
          <FacetColumn
            heading="By Brand"
            facets={facets.brand}
            paramKey="brand"
            spanClass="col-span-2"
            onLinkClick={close}
          />
          {/* Column: Model */}
          <FacetColumn
            heading="By Model"
            facets={facets.model}
            paramKey="model"
            spanClass="col-span-3"
            onLinkClick={close}
          />
          {/* Column: Collab */}
          <FacetColumn
            heading="By Collab"
            facets={facets.collab}
            paramKey="collab"
            spanClass="col-span-2"
            onLinkClick={close}
          />
          {/* Column: Size */}
          <div className="col-span-2">
            <div className="mono-strip mb-4 text-[var(--color-foil)]">By Size</div>
            <div className="grid grid-cols-3 gap-1.5">
              {facets.size.map((f) => (
                <Link
                  key={f.value}
                  href={`/shop?size=${encodeURIComponent(f.value)}`}
                  role="menuitem"
                  onClick={close}
                  className="rounded-sm border border-[var(--color-bone)]/15 px-2 py-1.5 text-center font-mono text-xs text-[var(--color-bone)] transition hover:border-[var(--color-blood)] hover:text-[var(--color-blood)]"
                >
                  {f.value}
                </Link>
              ))}
            </div>
          </div>
          {/* Featured cell — driven by the Wix "New Drop" category (server-side) */}
          {featured ? (
          <aside className="col-span-3 flex flex-col justify-between rounded-sm border border-[var(--color-foil)]/40 bg-[var(--color-blood)]/10 p-5">
            <div>
              <div className="mono-strip text-[var(--color-foil)]">
                {t({ en: "Featured drop", es: "Drop destacado" })}
              </div>
              <h3 className="font-display mt-2 text-xl uppercase leading-tight text-[var(--color-blood)]">
                {featured.title}
              </h3>
              {featured.sizesLabel ? (
                <p className="mt-3 text-xs text-[var(--color-bone-dim)]">
                  {t({
                    en: `Sizes ${featured.sizesLabel} in stock now. In-hand, shipped from the Bronx.`,
                    es: `Tallas ${featured.sizesLabel} disponibles ahora. En mano, enviadas desde el Bronx.`,
                  })}
                </p>
              ) : null}
            </div>
            <Link
              href={`/shop/${featured.id}`}
              role="menuitem"
              onClick={close}
              className="mono-strip mt-4 inline-flex w-fit items-center gap-2 rounded-sm bg-[var(--color-bone)] px-4 py-2 text-[10px] text-[var(--color-ink)] transition hover:bg-[var(--color-blood)] hover:text-[var(--color-bone)]"
            >
              {t({ en: "Reserve a pair →", es: "Reserva un par →" })}
            </Link>
          </aside>
          ) : null}
        </div>
        {/* Footer strip: full catalog */}
        <div className="border-t border-[var(--color-bone)]/10">
          <div className="mx-auto flex max-w-[1400px] items-center justify-between px-6 py-3">
            <Link
              href="/shop"
              role="menuitem"
              onClick={close}
              className="mono-strip text-[var(--color-bone-dim)] hover:text-[var(--color-foil)]"
            >
              {t({ en: "View entire catalog →", es: "Ver el catálogo →" })}
            </Link>
            <span className="mono-strip text-[var(--color-bone-dim)]">
              {t({
                en: "Free shipping on orders over $200",
                es: "Envío gratis en pedidos sobre $200",
              })}
            </span>
          </div>
        </div>
      </div>
    </div>
  );
}

function FacetColumn({
  heading,
  facets,
  paramKey,
  spanClass,
  onLinkClick,
}: {
  heading: string;
  facets: { value: string; label: string; count: number }[];
  paramKey: string;
  spanClass: string;
  onLinkClick: () => void;
}) {
  if (facets.length === 0) return null;
  return (
    <div className={spanClass}>
      <div className="mono-strip mb-4 text-[var(--color-foil)]">{heading}</div>
      <ul className="space-y-2">
        {facets.map((f) => (
          <li key={f.value}>
            <Link
              href={`/shop?${paramKey}=${encodeURIComponent(f.value)}`}
              role="menuitem"
              onClick={onLinkClick}
              className="group flex items-baseline justify-between gap-2 text-sm text-[var(--color-bone)] transition hover:text-[var(--color-blood)]"
            >
              <span className="font-display uppercase">{f.label}</span>
              <span className="mono-strip text-[var(--color-bone-dim)] transition group-hover:text-[var(--color-blood)]">
                {f.count}
              </span>
            </Link>
          </li>
        ))}
      </ul>
    </div>
  );
}

function Chevron({ className }: { className?: string }) {
  return (
    <svg viewBox="0 0 12 8" fill="none" className={className} aria-hidden="true">
      <path d="M1 1l5 5 5-5" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}
